import * as yup from "yup";
const emailRules = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
const zipCodeRegExp = /^[0-9]{4,10}$/; // Only numeric zipcodes between 4 to 10 digits
const sqlInjectionPattern = /(select|insert|update|delete|drop|truncate|exec|--|;|'|"|\/\*|\*\/)/i;

const preventSQLInjection = (fieldName) =>
  yup
    .string()
    .test("no-sql-injection", `${fieldName} contains invalid characters`, (value) => {
      if (!value) return true;
      return !sqlInjectionPattern.test(value);
    });

// This function will be used to validate if a city is in the list of valid cities
const isValidCity = (cityValue, validCities) => {
  if (!cityValue) return true; // Empty is valid (handled by required check)
  if (!validCities || !Array.isArray(validCities)) return true; // If no valid cities provided, assume valid
  
  return validCities.some(city => city.cityName === cityValue);
};

export const companySchema = yup.object().shape({
  image: yup
    .mixed()
    .test("fileFormat", "Unsupported file format", (value) => {
      if (!value) return true; // Not required
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      return allowedTypes.includes(value.type);
    }),

  companyName: yup
    .string()
    .trim()
    .max(255, "Company name too long")
    .required("Company name required")
    .concat(preventSQLInjection("Company Name")),

  firstName: yup
    .string()
    .trim()
    .max(255, "First name too long")
    .required("First name is required")
    .concat(preventSQLInjection("First Name")),

  lastName: yup
    .string()
    .trim()
    .notRequired()
    .concat(preventSQLInjection("Last Name")),

  contactNumber: yup
    .string()
    .required("Contact number required")
    .matches(phoneRegExp, "Contact number is not valid"),

  email: yup
    .string()
    .trim()
    .required("Email required")
    .matches(emailRules, { message: "Not valid email" }),

  companyAddress: yup
    .string()
    .trim()
    .max(500, "Address too long")
    .notRequired()
    .concat(preventSQLInjection("Company Address")),

  city: yup
    .string()
    .trim()
    .required("City is required")
    .max(100, "City too long")
    .test(
      'is-valid-city',
      'Please select a city from the list',
      function(value) {
        // Access the context to get the options from elsewhere
        // This will be populated at runtime
        const { options } = this.options.context || {};
        if (!value) return false; // Empty is invalid (city is required)
        return isValidCity(value, options?.validCities);
      }
    )
    .concat(preventSQLInjection("City Name")),

  state: yup
    .string()
    .trim()
    .notRequired()
    .when("state", (val, schema) =>
      val ? schema.required("State is required").max(100, "State too long") : schema
    ).concat(preventSQLInjection("State Name")),

  zipcode: yup
    .string()
    .trim()
    .notRequired()
    .when("zipcode", (val, schema) =>
      val
        ? schema
          .required("Zipcode is required")
          .matches(zipCodeRegExp, "Zipcode must be numeric and 4–10 digits")
        : schema
    ).concat(preventSQLInjection("Zipcode")),

  country: yup
    .string()
    .trim()
    .notRequired()
    .when("country", (val, schema) =>
      val ? schema.required("Country is required").max(100, "Country too long") : schema
    ).concat(preventSQLInjection("Country Name")),
});

export const createCompanySchema = yup.object().shape({
  image: yup.mixed().notRequired(),
  companyName: yup.string().required("Company name required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  contactNumber: yup
    .string()
    .required("Contact number required")
    .matches(phoneRegExp, "Contact number is not valid")
    .min(10, "Contact number must be at least 10 characters"),
  email: yup
    .string()
    .required("Email required")
    .matches(emailRules, { message: "Not valid email" }),
  password: yup.string().required('Password is required'),
  passwordConfirmation: yup.string().oneOf([yup.ref('password'), null], 'Passwords must match'),
  subscriptionId: yup.string().required("Subscription Code is required"),
});

export const updateCompanySchema = yup.object().shape({
  image: yup.mixed().notRequired(),
  companyName: yup.string().required("Company name required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  contactNumber: yup
    .string()
    .required("Contact number required")
    .matches(phoneRegExp, "Contact number is not valid")
    .min(10, "Contact number must be at least 10 characters"),
  email: yup
    .string()
    .required("Email required")
    .matches(emailRules, { message: "Not valid email" }),
  subscriptionId: yup.string().required("Subscription Code is required"),
})

export const emailConfigSchema = yup.object().shape({
  password: yup.string().required("password required"),
  emailId: yup
    .string()
    .required("Email required")
    .matches(emailRules, { message: "Not valid email" }),
  smtpPort: yup.number()
    .typeError("Port must be a number")
    .required("SMTP Port is required")
    .positive("Port must be positive")
    .integer("Port must be an integer"),
  emailType: yup.string().required("Company email type required"),
  smtpDetails: yup.string().required("Company smtp details required"),
});



const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const cinRegex = /^[A-Z0-9]{21}$/;
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const iecRegex = /^[A-Z0-9]{10}$/; // Typically 10 alphanumeric chars


export const agentTaxDetailsSchema = yup.object().shape({
  pan: yup
    .string()
    .trim()
    .notRequired()
    .matches(panRegex, "Invalid PAN format")
    .concat(preventSQLInjection("PAN Number")),

  cin: yup
    .string()
    .trim()
    .notRequired()
    .matches(cinRegex, "CIN must be 21 alphanumeric characters")
    .concat(preventSQLInjection("CIN Number")),

  transporterId: yup
    .string()
    .trim()
    .notRequired()
    .max(50, "Transporter ID too long")
    .concat(preventSQLInjection("Transporter ID")),

  iecNumber: yup
    .string()
    .trim()
    .notRequired()
    .matches(iecRegex, "Invalid IEC Number format")
    .concat(preventSQLInjection("IEC Number")),

  no: yup
    .string()
    .required("Gst Number is required")
    .trim()
    .matches(gstRegex, "Invalid GST Number format")
    .concat(preventSQLInjection("GST Number")),

  placeOfService: yup
    .string()
    .trim()
    .required("Place of Service is required")
    .max(100, "Place of Service too long")
    .concat(preventSQLInjection("Place of Service")),
});

export const shipperTaxDetailsSchema = yup.object().shape({
  pan: yup
    .string()
    .trim()
    .notRequired()
    .matches(panRegex, "Invalid PAN format")
    .concat(preventSQLInjection("PAN Number")),

  cin: yup
    .string()
    .trim()
    .notRequired()
    .matches(cinRegex, "CIN must be 21 alphanumeric characters")
    .concat(preventSQLInjection("CIN Number")),

  transporterId: yup
    .string()
    .trim()
    .notRequired()
    .max(50, "Transporter ID too long")
    .concat(preventSQLInjection("Transporter ID")),

  iecNumber: yup
    .string()
    .required("IEC Number is required")
    .trim()
    .matches(iecRegex, "Invalid IEC Number format")
    .concat(preventSQLInjection("IEC Number")),

  no: yup
    .string()
    .required("Gst Number is required")
    .trim()
    .matches(gstRegex, "Invalid GST Number format")
    .concat(preventSQLInjection("GST Number")),

  placeOfService: yup
    .string()
    .trim()
    .required("Place of Service is required")
    .max(100, "Place of Service too long")
    .concat(preventSQLInjection("Place of Service")),
});

const safeRequiredString = (fieldLabel) =>
  yup
    .string()
    .required(`${fieldLabel} is required`)
    .test(
      "no-sql-injection",
      `${fieldLabel} contains invalid characters`,
      (value) => !sqlInjectionPattern.test(value || "")
    );

export const addNewGstSchema = yup.object().shape({
  address: safeRequiredString("Company Address"),
  city: safeRequiredString("City"),
  state: safeRequiredString("State"),
  pinCode: yup
    .string()
    .required("pinCode is required")
    .matches(zipCodeRegExp, "pinCode must be 5 or 6 digits")
    .test(
      "no-sql-injection",
      "pinCode contains invalid characters",
      (value) => !sqlInjectionPattern.test(value || "")
    ),
  country: safeRequiredString("Country"),
  no: yup
    .string()
    .required("GST Number is required")
    .matches(gstRegex, "Invalid GST Number format")
    .test(
      "no-sql-injection",
      "GST Number contains invalid characters",
      (value) => !sqlInjectionPattern.test(value || "")
    ),
  placeOfService: safeRequiredString("Place of Supply"),
});